import {
  nonNullishOrFallback,
  nonNullishOrThrow,
} from '@web-platform/shared-universal-utils-frontend/src/helpers/assertNonNullish';
import { formatMoney } from '@web-platform/shared-universal-utils-frontend/src/helpers/formatMoney';
import { logError } from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';

import { mapSonarV3PricingItems } from './sonarV3PricingItemMapper';
import { mapSonarV3PricingOptionFare } from './sonarV3PricingOptionFareMapper';
import { mapUpdateStatus, priceUpdateStatusMap } from './sonarV3UpdateStatusMapper';

import type { PersonalItemAssessmentOutcome } from './sonarV3PricingOptionFareMapper';
import type {
  SkyscannerSonarCommonV1Enums,
  SkyscannerSonarV3Response,
} from '@skyscanner-internal/sonar-web-http-client';
import type { PricingOptionBookingPanel } from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/flights/itinerariesBookingPanel';
import type { PricingOptionFareTypeBookingPanel } from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/flights/pricingOptionBookingPanel';

type FareType = SkyscannerSonarCommonV1Enums.FareType;
type UnpricedType = SkyscannerSonarCommonV1Enums.UnpricedType;
type PricingOption = SkyscannerSonarV3Response.PricingOption;
type PricingItem = SkyscannerSonarV3Response.PricingItem;

// Mapping to conductor strings for now, when we split state this may become a boolean.
const unpricedTypeMap = {
  UNPRICED_TYPE_UNPRICED_ITINERARY: 'unpriced_itinerary',
  UNPRICED_TYPE_LIVE_UPDATE_DISABLED: 'lu_disabled',
  UNPRICED_TYPE_LIVE_UPDATE_FAILED: 'lu_failed',
  UNPRICED_TYPE_ITINERARY_NOT_RETURNED: 'itinerary_not_returned',
  UNPRICED_TYPE_PARTNER_PENDING: 'partner_pending',
  UNPRICED_TYPE_PARTNER_NOT_SELECTED: 'partner_not_selected',
  UNPRICED_TYPE_LIVE_UPDATE_ON_BOOKING_PANEL_ONLY: 'lu_on_bp_only',
  UNPRICED_TYPE_UNSPECIFIED: '',
} as const;

const mapUnpricedType = (unpricedType: UnpricedType | undefined): string =>
  unpricedType ? unpricedTypeMap[unpricedType] : '';

const fareTypeMap: Partial<Record<FareType, PricingOptionFareTypeBookingPanel>> = {
  FARE_TYPE_BASE_FARE: 'BASE_FARE',
  FARE_TYPE_BRANDED_FARE_UPSELL: 'BRANDED_FARE_UPSELL',
  FARE_TYPE_CONSTRUCTED_BASE_FARE: 'CONSTRUCTED_BASE_FARE',
  FARE_TYPE_CONSTRUCTED_BRANDED_FARE_UPSELL: 'CONSTRUCTED_BRANDED_FARE_UPSELL',
};

const mapFareType = (
  fareType: FareType | undefined,
): PricingOptionFareTypeBookingPanel | undefined => fareType && fareTypeMap[fareType];

const mapAgentIds = (pricingItems: PricingItem[]): string[] =>
  pricingItems.map((item) => nonNullishOrThrow(item.agent?.id, 'item.agent.id'));

export const mapSonarV3PricingOptions = (
  pricingOptions: PricingOption[],
  personalItemOutcome: PersonalItemAssessmentOutcome = 'INCLUDED',
): PricingOptionBookingPanel[] =>
  pricingOptions.reduce<PricingOptionBookingPanel[]>((options, option) => {
    try {
      return options.concat({
        agent_ids: mapAgentIds(nonNullishOrThrow(option.pricingItems, 'option.pricingItems')),
        id: nonNullishOrThrow(option.id, 'option.id'),
        items: mapSonarV3PricingItems(option.pricingItems),
        price: option.price
          ? {
              amount: formatMoney(option.price, false),
              update_status: mapUpdateStatus(priceUpdateStatusMap, option.updateStatus),
            }
          : undefined,
        pricing_option_fare: mapSonarV3PricingOptionFare(option.fare, personalItemOutcome),
        pricing_option_fare_type: mapFareType(option.fareType),
        unpriced_type: mapUnpricedType(option.unpricedType),
        isUpgradedOption: nonNullishOrFallback(option.upgradeOption, false, 'upgradeOption'),
        ...(option.discountedOfferId && typeof option.discountedOfferId === 'string'
          ? { discountedOfferId: option.discountedOfferId }
          : {}),
        ...(option.linkedItinerary ? { linked_itinerary: true } : {}),
      });
    } catch (error) {
      logError({
        component: 'mapSonarV3PricingOptions',
        data: JSON.stringify(option),
        error,
        message: `SONARV3: ${error.message}`,
      });
      return options;
    }
  }, []);
